

import { useTranslation } from '@fluxora/i18n';
import {
  Alert,
  Button,
  Card,
  CardBody,
  Label,
  ListboxSelect,
  Spinner,
  type SelectOption,
} from '@fluxora/ui';
import type { AddressSuggestion } from '@rheora/enrollment-contract';
import { useEffect, useMemo, useState } from 'react';

import { useEsiidLookup } from '../api/esiid.queries';
import {
  useAddressSuggestQuery,
  useEnsureSessionQuery,
} from '../api/serviceability.queries';
import { isValidZip } from '../services/funnelMethod';
import { useFunnel } from '../state/FunnelProvider';

import { NewServiceDeclaration } from './NewServiceDeclaration';
import { ResolvedMeterPanel } from './ResolvedMeterPanel';
import { ZipCombobox } from './ZipCombobox';

/**
 * "Shop by ZIP" on-ramp — meter-first landing (§3 of the epic).
 *
 * ONE server hop (fully BFF-local): the shopper types a ZIP; when it becomes 5
 * digits an address `Select` (until then DISABLED) is populated from
 * `GET /bff/address/suggest?q=<zip>`. EACH suggestion carries its catalog `esiid`,
 * so on pick we call `POST /bff/esiid/lookup { esiid }` DIRECTLY → the SAME
 * enriched `MeterDetail` the ESI ID on-ramp produces (one `MeterSummaryCard`).
 * There is NO `/bff/address/lookup` call in this flow anymore — `esiid/lookup` is
 * the single source of meter detail, and both endpoints are BFF mocks/stubs (no
 * GMO dependency in the ZIP flow).
 *
 * States: an UNKNOWN ZIP (no serviceable addresses) shows a "check the ZIP"
 * helper; the ESI ID lookup handles `not_found` / error with a helpful message.
 * The `Select` displays ONLY the address text — the esiid rides in the option
 * data, never rendered.
 *
 * ── AC3 ────────────────────────────────────────────────────────────────────
 * A funnel session is minted on mount (`useEnsureSessionQuery`). ⚠️ The esiid now
 * rides on the public suggest response (a deliberate DUMMY-flow relaxation — see
 * STUBS.md); the ESI ID is still a display/lookup INPUT to `/bff/esiid/lookup`,
 * which mints the opaque `candidateId` the browser holds.
 * // TODO(SHOP-1): if suggest ever goes real, drop the esiid from it and resolve
 * // server-side again (session-scoped), restoring the never-enumerable posture.
 */
export function ZipSubflow(): JSX.Element {
  const { t } = useTranslation();
  const funnel = useFunnel();

  const session = useEnsureSessionQuery();
  // Suggest is now SESSION-gated on the BFF (it returns esiids) — only query it
  // once the funnel session is minted (`session.data` present), so the suggest GET
  // always carries the session cookie and never races the session mint into a 401.
  const suggestQuery = useAddressSuggestQuery(
    session.data !== undefined && funnel.zip !== undefined && isValidZip(funnel.zip) ? funnel.zip : '',
  );
  const esiidLookup = useEsiidLookup();

  const [selectedSuggestionId, setSelectedSuggestionId] = useState<string>('');

  // ZIP input is controlled by the funnel store so a tab-switch retains it.
  const setZip = (rawValue: string): void => {
    const cleaned = rawValue.replace(/\D/gu, '').slice(0, 5);
    funnel.setZip(cleaned.length === 0 ? undefined : cleaned);
    setSelectedSuggestionId('');
    if (funnel.meterDetail !== undefined) funnel.clearResolved();
  };

  const zip = funnel.zip ?? '';
  const zipReady = isValidZip(zip);

  const suggestions: readonly AddressSuggestion[] = suggestQuery.data?.suggestions ?? [];
  const scopedSuggestions = useMemo(
    () => (zipReady ? suggestions.filter((s) => s.zip === zip) : []),
    [suggestions, zip, zipReady],
  );

  // The Select shows ONLY the address text; the esiid is carried in `scopedSuggestions`.
  const options: SelectOption[] = useMemo(
    () =>
      scopedSuggestions.map((s) => ({
        value: s.id,
        label: `${s.line1} — ${s.city}, ${s.state} ${s.zip}`,
      })),
    [scopedSuggestions],
  );

  const pickedSuggestion = scopedSuggestions.find((s) => s.id === selectedSuggestionId);
  const selectDisabled = !zipReady || suggestQuery.isFetching || options.length === 0;

  // On pick → look the meter up DIRECTLY by the suggestion's catalog esiid. No
  // `/bff/address/lookup` hop.
  const handleCheck = (): void => {
    if (pickedSuggestion?.esiid === undefined) return;
    esiidLookup.mutate({ esiid: pickedSuggestion.esiid });
  };

  // When the ESI ID lookup resolves, hoist the meter + opaque handle into the store.
  useEffect(() => {
    if (esiidLookup.isSuccess && esiidLookup.data.outcome === 'resolved') {
      funnel.setResolved(esiidLookup.data.candidateId, esiidLookup.data.meter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esiidLookup.isSuccess, esiidLookup.data]);

  // Session prerequisite (AC3).
  if (session.isPending) {
    return (
      <div className="flex justify-center py-8">
        <Spinner size="lg" label={t('shop.address.session.loading')} />
      </div>
    );
  }
  if (session.isError) {
    return (
      <Alert variant="danger" title={t('shop.address.session.error.title')} role="alert">
        {t('shop.address.session.error.body')}
      </Alert>
    );
  }

  // Resolved — the shared card takes over (defensive fallback; the landing page
  // renders it too).
  if (funnel.meterDetail !== undefined) {
    return (
      <ResolvedMeterPanel meterDetail={funnel.meterDetail} onStartOver={() => funnel.clearResolved()} />
    );
  }

  const meterResolving = esiidLookup.isPending;
  const meterResolveFailed =
    esiidLookup.isError || (esiidLookup.isSuccess && esiidLookup.data.outcome === 'not_found');

  return (
    <div className="flex flex-col gap-4">
      <Card variant="glass" padding="md">
        <CardBody className="flex flex-col gap-3 p-0">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(140px,180px)_1fr]">
            <div className="flex flex-col gap-1">
              <ZipCombobox
                id="zip-subflow-zip"
                label={t('shop.landing.zip.zip.label')}
                value={zip}
                onValueChange={setZip}
                sessionReady={session.data !== undefined}
              />
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="zip-subflow-address">{t('shop.landing.zip.address.label')}</Label>
              {/* The COMMON listbox — our panel, not the OS menu (the native
                  <select> popup ignored the design system entirely). */}
              <ListboxSelect
                id="zip-subflow-address"
                options={options}
                placeholder={
                  zipReady ? t('shop.landing.zip.address.placeholder') : t('shop.landing.zip.address.awaitingZip')
                }
                value={selectedSuggestionId}
                onChange={setSelectedSuggestionId}
                disabled={selectDisabled}
              />
              {zipReady && !suggestQuery.isFetching && suggestQuery.isError ? (
                <span id="zip-subflow-address-help" className="text-xs text-foreground/60">
                  {t('shop.landing.zip.address.error')}
                </span>
              ) : zipReady &&
                !suggestQuery.isFetching &&
                suggestQuery.isSuccess &&
                options.length === 0 ? (
                <span id="zip-subflow-address-help" className="text-xs text-foreground/60">
                  {t('shop.landing.zip.address.empty', { zip })}
                </span>
              ) : null}
            </div>
          </div>

          <Button
            variant="primary"
            shape="pill"
            onClick={handleCheck}
            disabled={pickedSuggestion === undefined || meterResolving}
          >
            {meterResolving
              ? t('shop.landing.meter.resolving')
              : t('shop.landing.zip.check.cta')}
          </Button>
        </CardBody>
      </Card>

      {meterResolving ? (
        <div className="flex justify-center py-2">
          <Spinner size="md" label={t('shop.landing.meter.resolving')} />
        </div>
      ) : null}

      {meterResolveFailed ? (
        <Alert variant="danger" title={t('shop.landing.meter.resolveError.title')} role="alert">
          {t('shop.landing.meter.resolveError.body')}
        </Alert>
      ) : null}

      {/* ── E-SF20 — the new-construction path (US-01) ─────────────────────────
          THIS is the live dead end the epic exists to remove. The address Select
          is populated from ESI IDs, so a brand-new premise can never appear in
          it: the customer either sees an empty list, or a list of their
          neighbours without their own address. Both used to end here.

          Offered whenever the ZIP is a real 5 digits — NOT only when the list
          came back empty. A new subdivision inside an established ZIP returns
          plenty of suggestions and still cannot return theirs, and that customer
          is exactly as stuck as the one who saw nothing.

          It stays COLLAPSED behind its own affordance, which is the
          duplicate-declaration guard (US-01 AC2): picking an address from the
          list is the first offer, and telling us it is a new build is the
          second. Suppressed while the list is still loading, so it never appears
          as the answer to a question that has not finished being asked. */}
      {zipReady && !suggestQuery.isFetching ? <NewServiceDeclaration zip={zip} /> : null}
    </div>
  );
}
